import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
const destinations = [{
  src: 'https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=800&h=1000&fit=crop&q=80',
  title: 'La Gran Muralla',
  location: 'Pekín'
}, {
  src: 'https://images.unsplash.com/photo-1584952296744-566e0e3e5e52?w=800&h=1000&fit=crop&q=80',
  title: 'Ciudad Prohibida',
  location: 'Pekín'
}, {
  src: 'https://images.unsplash.com/photo-1538428494232-9c0d8a3ab403?w=800&h=1000&fit=crop&q=80',
  title: 'Pudong Skyline',
  location: 'Shanghái'
}, {
  src: 'https://images.unsplash.com/photo-1537531383496-f4749b918cbe?w=800&h=1000&fit=crop&q=80',
  title: 'Montañas Karst',
  location: 'Guilin'
}, {
  src: 'https://images.unsplash.com/photo-1528164344885-1d40d64b5c5f?w=800&h=1000&fit=crop&q=80',
  title: 'Río Li',
  location: 'Yangshuo'
}, {
  src: 'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?w=800&h=1000&fit=crop&q=80',
  title: 'Templo del Cielo',
  location: 'Pekín'
}];
export function ChinaGallery() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const getCardWidth = useCallback(() => {
    if (!scrollRef.current) return 280;
    const firstCard = scrollRef.current.querySelector('[data-card]') as HTMLElement;
    if (!firstCard) return 280;
    return firstCard.offsetWidth + 20;
  }, []);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const cardWidth = getCardWidth();
      const scrollLeft = container.scrollLeft;
      const index = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(Math.max(index, 0), destinations.length - 1));
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [getCardWidth]);
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const cardWidth = getCardWidth();
    scrollRef.current.scrollTo({
      left: index * cardWidth,
      behavior: 'smooth'
    });
    setActiveIndex(index);
  };
  return <section className="py-10 lg:py-16 bg-charcoal overflow-hidden" data-id="element-13">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 lg:mb-10" data-id="element-14">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-15">
          <span className="text-gold font-medium tracking-wider text-xs lg:text-sm uppercase" data-id="element-16">
            Destinos que te esperan
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2 lg:mt-3 font-serif" data-id="element-17">
            Lo que vas a vivir en China
          </h2>
        </motion.div>
      </div>

      <div className="relative" data-id="element-18">
        <div ref={scrollRef} className="flex gap-4 lg:gap-5 overflow-x-auto pb-4 px-4 sm:px-6 lg:px-8 snap-x snap-mandatory" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-19">
          {destinations.map((dest, index) => <motion.div key={index} data-card initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08,
          duration: 0.4
        }} className="flex-shrink-0 w-60 sm:w-72 lg:w-80 snap-center group relative rounded-2xl overflow-hidden" data-id="element-20">
              <div className="aspect-[3/4] relative bg-charcoal-light" data-id="element-21">
                <img src={dest.src} alt={`${dest.title}, ${dest.location}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" data-id="element-22" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" data-id="element-23" />
                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6" data-id="element-24">
                  <p className="text-gold text-xs lg:text-sm font-medium tracking-wider uppercase mb-1" data-id="element-25">
                    {dest.location}
                  </p>
                  <h3 className="text-white text-lg lg:text-xl font-bold font-serif" data-id="element-26">
                    {dest.title}
                  </h3>
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="absolute top-0 left-0 w-8 lg:w-12 h-full bg-gradient-to-r from-charcoal to-transparent pointer-events-none z-10" data-id="element-27" />
        <div className="absolute top-0 right-0 w-8 lg:w-12 h-full bg-gradient-to-l from-charcoal to-transparent pointer-events-none z-10" data-id="element-28" />
      </div>

      {/* Dot indicators */}
      <div className="flex items-center justify-center gap-2 mt-5 lg:mt-8" data-id="element-29">
        {destinations.map((_, index) => <button key={index} onClick={() => scrollToIndex(index)} aria-label={`Ir a foto ${index + 1}: ${destinations[index].title}`} className="group p-1" data-id="element-30">
            <motion.div animate={{
          width: activeIndex === index ? 24 : 8,
          backgroundColor: activeIndex === index ? '#C5A55A' : 'rgba(255,255,255,0.3)'
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="h-2 rounded-full" data-id="element-31" />
          </button>)}
      </div>
    </section>;
}